import React from "react";
import { NavLink, Outlet, Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Newspaper, Users, Building2, Mail, Calendar, Sparkles, Compass, ExternalLink } from "lucide-react";
import { LOGO_URL, SITE } from "@/lib/config";

const items = [
    { to: "/admin", label: "Panel", icon: Newspaper, end: true },
    { to: "/admin/news", label: "Noticias", icon: Newspaper },
    { to: "/admin/tarifa-news", label: "Tarifa en medios", icon: Sparkles },
    { to: "/admin/weekly-plans", label: "Qué hacer esta semana", icon: Compass },
    { to: "/admin/events", label: "Eventos", icon: Calendar },
    { to: "/admin/board", label: "Junta directiva", icon: Users },
    { to: "/admin/directory", label: "Directorio", icon: Building2 },
    { to: "/admin/messages", label: "Mensajes", icon: Mail },
];

export default function AdminLayout() {
    const { user, initialized, logout } = useAuth();
    const navigate = useNavigate();

    if (!initialized) {
        return <div data-testid="admin-loading" className="p-10 text-[#4A5568]">Cargando...</div>;
    }
    if (!user || user.role !== "admin") {
        return <Navigate to="/admin/login" replace />;
    }

    const onLogout = async () => {
        await logout();
        navigate("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] flex flex-col lg:flex-row" data-testid="admin-layout">
            <aside className="lg:w-64 bg-[#082050] text-white lg:min-h-screen">
                <div className="p-6 border-b border-white/10">
                    <Link to="/" className="flex items-center" aria-label={`${SITE.fullName} — Ir al inicio`}>
                        <img src={LOGO_URL} alt={`Logo oficial de ${SITE.fullName}`} className="h-8 w-auto" width="130" height="32" />
                    </Link>
                    <div className="text-xs text-white/50 mt-1">Panel administración</div>
                </div>
                <nav className="p-4 flex lg:flex-col gap-1 overflow-x-auto">
                    {items.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            end={it.end}
                            data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 text-sm whitespace-nowrap ${
                                    isActive ? "bg-white text-[#082050]" : "text-white/80 hover:bg-white/10"
                                }`
                            }
                        >
                            <it.icon size={16} />
                            {it.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-white/10 mt-auto space-y-2 hidden lg:block">
                    <a href="/" className="flex items-center gap-2 text-xs text-white/60 hover:text-white px-4 py-2">
                        <ExternalLink size={12} /> Ver sitio público
                    </a>
                    <button
                        onClick={onLogout}
                        data-testid="btn-admin-logout"
                        className="w-full flex items-center gap-2 text-xs text-white/60 hover:text-white px-4 py-2"
                    >
                        <LogOut size={12} /> Cerrar sesión
                    </button>
                </div>
            </aside>
            <main className="flex-1 min-w-0">
                <div className="lg:hidden bg-white border-b border-[#E2E4E9] px-4 py-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#082050]">Administración</span>
                    <button
                        onClick={onLogout}
                        data-testid="btn-admin-logout-mobile"
                        className="text-xs text-[#4A5568] flex items-center gap-1"
                    >
                        <LogOut size={12} /> Salir
                    </button>
                </div>
                <div className="p-6 sm:p-10">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
